import React, { Suspense, lazy, useEffect, useState } from 'react';
import proptypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { Row, Col } from '@thd-olt-component-react/core-ui';
import { QueryProvider, extend } from '@thd-nucleus/data-sources';
import {
  DndContext, useSensor, useSensors, MouseSensor, TouchSensor
} from '@dnd-kit/core';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import ZoneCard from '../zone-card';
import Configurator from '../../components/Configurator';
import Visualizer from '../../components/Visualizer';
import DraggableOverlay from '../../components/DraggableOverlay';
import { useAsyncServiceGCC } from '../../hooks';
import { useCallout } from '../../hooks/useCallout';
import { getProductInfo } from '../../api';
import {
  useProductInfoStore,
  useDropZoneStore, useConfiguratorStore,
  useProductDataStore, useDigitalDataStore
} from '../../store';
import { useDesignStore } from '../../store/design/context';
import Orchestrator from '../../components/Orchestrator';
import PipPersistLoader from '../../components/Orchestrator/loaders/PipPersist';
import { getLocalizerZipCode } from '../../utils/fulfillment-utils';
import PromotionsProvider from '../../components/PromotionsProvider';
import MediaAssetsProvider from '../../components/MediaAssetsProvider';
import GraphDataOrchestrator from '../../components/GraphDataOrchestrator';
import { DrawerLoader } from '../../components/BundleDrawer';
import { handleDragEnd, handleDragStart, handleDragOver } from '../../dnd/handlers';
import { Callout } from '../../components/Visualizer/components';
import { DecisionModal } from '../vehicleStorage/components/DecisionModal';
import { STACK_BUILDER, WALL_STORAGE } from '../../constants';

const { string, shape } = proptypes;

const DesktopDrawerLazy = lazy(() => import('../../components/Drawer/Desktop'));
const MobileDrawerLazy = lazy(() => import('../../components/Drawer/Mobile'));

// promo utils

const Product = ({ store, itemId, pipOmsId, draftProductId }) => {
  const { storeId, storeZip } = store;
  const zipCode = getLocalizerZipCode();
  const [hydrated, setHydrated] = useState({
    design: false,
    graph: false,
  });
  const designAPIEnabled = useConfigService('design-api:enable');
  const [attributes] = useAsyncServiceGCC(getProductInfo, draftProductId || itemId);

  // configurator store
  const {
    configurator: { metadata },
  } = useConfiguratorStore((state) => state);

  // product data store (aka graphQL data)
  const storeProductData = useProductDataStore((state) => state.productData);

  // design store
  const {
    design: {
      selectedProduct,
      products,
    },
    bundles,
    select,
    resetSelected,
    add: addProduct,
    addBundleProducts,
    remove: removeProduct,
    selectVisualizerItem,
    handleDragEvent,
  } = useDesignStore((state) => state);

  // product info store
  const updateProductInfo = useProductInfoStore((state) => state.updateProductInfo);
  const productInfo = useProductInfoStore((state) => state.productInfo);

  // digital data store
  const {
    trackAddItemToStackEvent,
    trackRemoveItemsFromStackEvent
  } = useDigitalDataStore((state) => state);

  // dropzone store
  const resetDropZones = useDropZoneStore((state) => state.resetDropZones);

  const matches = useBreakpoint('lg');
  const isDesktop = matches.greater;
  const brand = productInfo?.brand ?? 'unknown';
  const productName = productInfo?.productName ?? 'builder';
  const builderName = `${brand} ${productName}`;
  const closeEventName = `leaving ${builderName}`;
  const CALLOUT_COPY = {
    rack: {
      headerTitle: productInfo?.callOutConfig?.rack?.header ?? STACK_BUILDER.PACKOUT.CALLOUT_HEADER,
      message: productInfo?.callOutConfig?.rack?.description ?? STACK_BUILDER.PACKOUT.CALLOUT_BODY,
      buttonLabel: STACK_BUILDER.PACKOUT.CALLOUT_BUTTON_TITLE,
href: productInfo?.callOutConfig?.rack?.href ?? STACK_BUILDER.PACKOUT.CALLOUT_DECISION_MODAL.REDIRECT_URL,
      imageSrc: productInfo?.callOutConfig?.rack?.image
    },
    wall: {
      headerTitle: productInfo?.callOutConfig?.wall?.header ?? WALL_STORAGE.PACKOUT.CALLOUT_HEADER,
      message: productInfo?.callOutConfig?.wall?.description ?? WALL_STORAGE.PACKOUT.CALLOUT_BODY,
      buttonLabel: WALL_STORAGE.PACKOUT.CALLOUT_BUTTON_TITLE,
      href: productInfo?.callOutConfig?.wall?.href ?? WALL_STORAGE.PACKOUT.REDIRECT_URL,
      imageSrc: productInfo?.callOutConfig?.wall?.image
    }
  };

  const targets = [];
  if (productInfo?.callOutConfig?.rack) {
    targets.push({
      key: 'rack',
      gate: { flag: 'portableStorageCallout' },
      analyticsConfig: {
        open: {
          feature: STACK_BUILDER.PACKOUT.CALLOUT_DECISION_MODAL.CANCEL_BUTTON_NAME,
          name: productInfo?.callOutConfig?.rack?.header ?? STACK_BUILDER.PACKOUT.CALLOUT_HEADER,
          eventName: `start ${(productInfo?.callOutConfig?.rack?.header
            ?? STACK_BUILDER.PACKOUT.CALLOUT_HEADER).replace('Build', 'building')}`
        },
        close: {
          feature: STACK_BUILDER.PACKOUT.CALLOUT_DECISION_MODAL.CANCEL_BUTTON_NAME,
          name: productInfo?.callOutConfig?.rack?.header ?? STACK_BUILDER.PACKOUT.CALLOUT_HEADER,
          eventName: closeEventName
        }
      }
    });
  }
  if (productInfo?.callOutConfig?.wall) {
    targets.push({
      key: 'wall',
      gate: { unconditional: true },
      analyticsConfig: {
        open: {
          feature: WALL_STORAGE.PACKOUT.CALLOUT_BUTTON_TITLE,
          name: productInfo?.callOutConfig?.wall?.header ?? WALL_STORAGE.PACKOUT.CALLOUT_HEADER,
          eventName: `start ${(productInfo?.callOutConfig?.wall?.header
            ?? WALL_STORAGE.PACKOUT.CALLOUT_HEADER).replace('Build', 'building')}`
        },
        close: {
          feature: STACK_BUILDER.PACKOUT.CALLOUT_DECISION_MODAL.CANCEL_BUTTON_NAME,
          name: productInfo?.callOutConfig?.wall?.header ?? WALL_STORAGE.PACKOUT.CALLOUT_HEADER,
          eventName: closeEventName
        }
      }
    });
  }
  const {
    callouts, activeKey, isDecisionModalOpen, handleDecisionModalClose
  } = useCallout({
    itemId,
    omsId: productInfo?.partnerProductId ?? STACK_BUILDER.PACKOUT.OMS_ID,
    targets
  });

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8
    },
    autoScrollEnabled: !isDesktop
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200,
      tolerance: 5,
    },
    autoScrollEnabled: !isDesktop
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  useEffect(() => {
    if (attributes.ready) {
      updateProductInfo(attributes?.data);
    }
  }, [attributes.ready]);

  return (
    <div className="grid">
      <ErrorBoundary name="eb-breadcrumbs">
        <QueryProvider cacheKey="breadcrumbs">
          <Row className="u--paddingTop">
            <Col className="sui-overflow-visible" fallback="12" flatten>
              <Breadcrumbs
                itemId={itemId}
                useStencilDesign
              />
            </Col>
          </Row>
        </QueryProvider>
      </ErrorBoundary>
      {(metadata && designAPIEnabled) && (
        <>
          <ErrorBoundary name="eb-orchestrator">
            <Orchestrator
              metadata={metadata}
              setHydrated={setHydrated}
              pipOmsId={pipOmsId}
            />
          </ErrorBoundary>
          <ErrorBoundary>
            <PipPersistLoader zipCode={zipCode} pipOmsId={pipOmsId} ready={hydrated.design && hydrated.graph} />
          </ErrorBoundary>
        </>
      )}
      <MediaAssetsProvider data={attributes} omsId={itemId} />
      {hydrated.design && (<GraphDataOrchestrator setHydrated={setHydrated} />)}
      {hydrated.graph && (<PromotionsProvider />)}
      <DndContext
        onDragEnd={
          (event) => handleDragEnd(
            event,
            {
              metadata,
              storeProductData,
              removeProduct,
              trackRemoveItemsFromStackEvent,
              selectedProduct,
              addProduct,
              addBundleProducts,
              trackAddItemToStackEvent,
              resetDropZones,
              resetSelected,
              handleDragEvent,
              bundles,
              products,
            }
          )
        }
        onDragStart={
          (event) => handleDragStart(
            event,
            {
              selectVisualizerItem,
              select,
              resetDropZones,
              resetSelected,
              selectedProduct,
              handleDragEvent
            }
          )
        }
        onDragOver={handleDragOver}
        sensors={sensors}
        autoScroll={false}
      >
        <Row>
          <Col id="visualizer-zone" className="visualizer-zone" fallback="12" xs="12" sm="6" md="6">
            <ErrorBoundary name="eb-visualizer">
              <Visualizer
                isDesktop={isDesktop}
                data={attributes}
                itemId={itemId}
                placeholder="visualizer"
                callouts={callouts}
                calloutCopy={CALLOUT_COPY}
              />
            </ErrorBoundary>
          </Col>
          <Col className="configurator-zone" fallback="12" xs="12" sm="6">
            <ErrorBoundary name="eb-configurator">
              <ZoneCard className="zone-card col__12-12">
                <Configurator
                  name="configurator"
                  itemId={draftProductId || itemId}
                  isDesktop={isDesktop}
                  selectedId={selectedProduct?.itemId}
                />
              </ZoneCard>
              {!isDesktop && (
                <div className="sui-flex sui-flex-col sui-w-full sui-p-2">
                  {callouts.map((callout) => callout.displayCallout && (
                    <Callout
                      key={callout.key}
                      headerTitle={CALLOUT_COPY[callout.key].headerTitle}
                      message={CALLOUT_COPY[callout.key].message}
                      buttonLabel={CALLOUT_COPY[callout.key].buttonLabel}
                      imageSrc={CALLOUT_COPY[callout.key].imageSrc}
                      onClick={callout.handleCalloutClick}
                      isDesktop={isDesktop}
                    />
                  ))}
                </div>
              )}
              <div className="sui-w-full sui-h-[10px] sui-bg-[#f5f5f5]" />
            </ErrorBoundary>
            <ErrorBoundary name="draggable-overlay">
              <DraggableOverlay selectedProduct={selectedProduct} />
            </ErrorBoundary>
          </Col>
        </Row>
      </DndContext>
      <DecisionModal
        headerTitle={STACK_BUILDER.PACKOUT.CALLOUT_DECISION_MODAL.HEADER_TITLE}
        message={STACK_BUILDER.PACKOUT.CALLOUT_DECISION_MODAL.MESSAGE}
        confirmButtonName={STACK_BUILDER.PACKOUT.CALLOUT_DECISION_MODAL.CONFIRM_BUTTON_NAME}
        cancelButtonName={STACK_BUILDER.PACKOUT.CALLOUT_DECISION_MODAL.CANCEL_BUTTON_NAME}
        isOpen={isDecisionModalOpen}
        onClose={handleDecisionModalClose}
        href={CALLOUT_COPY[activeKey]?.href}
        builderName={builderName}
      />
      {isDesktop && !_isEmpty(storeProductData) && (
        <Suspense fallback={<DrawerLoader />}>
          <DesktopDrawerLazy
            productData={storeProductData}
            isDesktop={isDesktop}
            host="https://www.homedepot.com"
            storeId={storeId}
            storeZip={storeZip}
            userZip={zipCode}
          />
        </Suspense>
      )}
      {!isDesktop && !_isEmpty(storeProductData) && (
        <Suspense fallback={<DrawerLoader />}>
          <MobileDrawerLazy
            productData={storeProductData}
          />
        </Suspense>
      )}
    </div>
  );
};

Product.propTypes = {
  store: shape({
    storeId: string,
    storeZip: string
  }).isRequired,
  pipOmsId: string,
  draftProductId: string,
  itemId: string.isRequired
};

Product.defaultProps = {
  pipOmsId: null,
  draftProductId: null
};

Product.displayName = 'ProductPage';

Product.dataModel = extend(
  Breadcrumbs
);

export default Product;
